// Site-wide static data for CNGTPL Solar
import { Home, Building2, Factory, Flame, Lightbulb, Wrench, Zap, BatteryCharging, Sun, Gauge, Droplets, Settings } from "lucide-react";

export const COMPANY = {
  name: "CNGTPL Solar",
  tagline: "Clean Energy • Powering Tomorrow",
  email: "enquiry@solar.cngtpl.com",
  phone: "+91 93156 49601",
  whatsapp: "919315649601",
  address: "26, Gurunanak Pura Road, Laxmi Nagar, New Delhi - 110092, India",
  hours: "Mon - Sat: 9:30 AM — 7:00 PM",
  founded: 2021,
  logo: "/logo.png",
  mapEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.0998!2d77.2773!3d28.6380!2m3!1f0!2f0!3f0!3m2!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM4JzE2LjgiTiA3N8KwMTYnMzguMyJF!5e0!3m2!1sen!2sin!4v1700000000000",
};

export const SERVICES = [
  {
    slug: "residential-solar",
    title: "Residential Solar",
    short: "Rooftop solar for homes",
    icon: Home,
    summary: "Power your home with clean energy. Cut your bills by up to 90% and enjoy 25 years of reliable generation.",
    benefits: ["90% bill reduction", "Govt subsidy up to ₹78,000", "25-year panel warranty", "Net metering support"],
    pricing: "Starting ₹55,000 / kW*",
    image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=70",
    details: "A residential solar system is the smartest home upgrade of the decade. For an average 3-bedroom Indian home consuming 400-600 units/month, a 3-5 kW system typically eliminates 80-95% of the bill. We handle the complete journey — feasibility survey, DISCOM net-metering application, MNRE-compliant installation, subsidy paperwork via PM Surya Ghar, and a 25-year performance warranty on panels.",
    specs: ["1 kW to 10 kW range", "Requires 100-120 sqft shadow-free area per kW", "Generates 4-5 units per day per kW", "Payback in 3-5 years", "Earn net-metering credits on excess generation"],
  },
  {
    slug: "commercial-solar",
    title: "Commercial Solar",
    short: "For offices, hotels, shops",
    icon: Building2,
    summary: "Lock in your commercial energy costs. Solar generates ROI within 3-4 years for most commercial establishments.",
    benefits: ["30-50% operating cost cut", "Accelerated depreciation", "Corporate tax benefits", "Scalable rooftop solutions"],
    pricing: "Starting ₹48,000 / kW*",
    image: "https://images.unsplash.com/photo-1611365892117-00ac5ef43c90?w=1200&q=70",
    details: "Commercial solar is one of the fastest-payback investments available to Indian businesses. With daytime consumption that perfectly matches solar generation, and tax benefits like 40% Accelerated Depreciation under Section 32, the effective payback is often under 3 years. We service offices, hotels, hospitals, schools, showrooms and warehouses across India.",
    specs: ["10 kW to 500 kW under net-metering", "SCADA + cloud monitoring included", "Zero-downtime installation", "Accelerated Depreciation benefit", "Optional OPEX / PPA models available"],
  },
  {
    slug: "industrial-solar",
    title: "Industrial Solar",
    short: "MW-scale plants",
    icon: Factory,
    summary: "MW-scale ground & rooftop plants engineered for industrial reliability and maximum yield.",
    benefits: ["MW scale engineering", "SCADA remote monitoring", "O&M contracts", "PPA / CAPEX / OPEX models"],
    pricing: "Custom Quote",
    image: "https://images.unsplash.com/photo-1613665813446-82a78c468a1d?w=1200&q=70",
    details: "Industrial solar plants from 500 kW to 10+ MW, designed for continuous high-throughput generation. Our engineering team handles site grading, structural foundations (for ground-mount), transformer and LT/HT yard design, CEA approvals, and commissioning with SCADA integration. Flexible financing — CAPEX, OPEX with zero investment or hybrid PPA models.",
    specs: ["500 kW to 10+ MW capacity", "Central + string inverter options", "SCADA + real-time data logging", "HT/LT metering + CEA paperwork", "25-year AMC contracts available"],
  },
  {
    slug: "on-grid-solar",
    title: "On-Grid Solar System",
    short: "Grid-tied with net metering",
    icon: Sun,
    summary: "Most popular residential & commercial choice. Connects to the DISCOM grid with net metering — sell excess units back.",
    benefits: ["Net metering enabled", "Lowest per-kW cost", "No battery required", "Best for day-heavy consumption"],
    pricing: "Starting ₹55,000 / kW*",
    image: "https://images.unsplash.com/photo-1548337138-e87d889cc369?w=1200&q=70",
    details: "An on-grid solar system is the most economical and popular solar setup in India. No batteries to maintain, no complex load management — simply synchronise with the grid and export excess generation for credits. Automatic anti-islanding protection ensures safety during grid outages (as per IEC 61727 / IEEE 1547).",
    specs: ["Capacity: 1 kW to 1 MW", "Eligibility: grid-connected premises", "Bi-directional meter required", "DISCOM approval timeline: 30-45 days", "Works only when grid is available"],
  },
  {
    slug: "off-grid-solar",
    title: "Off-Grid Solar System",
    short: "Standalone battery-backed",
    icon: BatteryCharging,
    summary: "Self-sufficient solar + battery system. Perfect for farms, villages and areas with frequent power cuts.",
    benefits: ["100% energy independence", "Works without grid", "Lithium or tall-tubular battery", "Ideal for remote areas"],
    pricing: "Starting ₹85,000 / kW*",
    image: "https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=1200&q=70",
    details: "An off-grid system gives you complete energy independence. Perfect for villages, remote farmhouses, telecom towers, border outposts, or anywhere with unreliable grid supply. Solar panels charge a battery bank during the day, which powers your loads 24×7. Larger battery capacity = longer autonomy.",
    specs: ["Capacity: 500W to 100 kW", "LiFePO4 or tall-tubular battery bank", "MPPT hybrid inverter with charge controller", "Autonomy: 1-3 days typical", "No DISCOM approval needed"],
  },
  {
    slug: "hybrid-solar",
    title: "Hybrid Solar System",
    short: "Grid-tied + battery backup",
    icon: Zap,
    summary: "Best of both worlds. Grid-connected with battery backup — runs your home even when grid fails.",
    benefits: ["Grid + battery backup", "Automatic failover", "Net metering eligible", "Smart load management"],
    pricing: "Starting ₹95,000 / kW*",
    image: "https://images.unsplash.com/photo-1559302995-f1c7ac2d9ec8?w=1200&q=70",
    details: "A hybrid system combines an on-grid solar system with a battery for power-cut backup. When the grid fails, the inverter automatically switches to battery in under 20ms so your essential loads (lights, fans, TV, Wi-Fi, fridge) keep running. Ideal for tier-2/3 cities where grid outages are common.",
    specs: ["2 kW to 50 kW capacity", "5-20 kWh battery bank", "Auto-switchover to battery in outages", "Net-metering compatible", "Smart app for load priority control"],
  },
  {
    slug: "solar-water-pump",
    title: "Solar Water Pump",
    short: "PM-KUSUM eligible",
    icon: Droplets,
    summary: "Solar-powered agricultural pumps. 60% Govt subsidy under PM-KUSUM scheme. 1 HP to 10 HP systems.",
    benefits: ["Up to 60% PM-KUSUM subsidy", "AC & DC surface / submersible", "BIS IS 17426 certified", "5-year warranty"],
    pricing: "Starting ₹1.2 Lakh / HP",
    image: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1200&q=70",
    details: "Solar water pumps transform farm economics. Under PM-KUSUM (Component-B / Component-C), farmers get up to 60% central + state subsidy. Zero diesel cost, zero electricity bill, zero grid dependency. Surface pumps for open wells and submersible pumps for borewells — both AC and DC options available.",
    specs: ["1 HP to 10 HP capacity", "Surface + Submersible models", "AC & DC VFD controllers", "Lift up to 200 metres", "Daily output: 20,000 – 2,00,000 litres"],
  },
  {
    slug: "solar-inverter",
    title: "Solar Inverter",
    short: "String, micro & hybrid",
    icon: Gauge,
    summary: "MNRE-approved string, microinverter and hybrid inverters. Growatt, Luminous, Microtek, Sungrow, Fronius and more.",
    benefits: ["5 kW — 100 kW capacity", "MPPT dual tracking", "Wi-Fi remote monitoring", "10-year warranty"],
    pricing: "Starting ₹18,000",
    image: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=1200&q=70",
    details: "The inverter is the heart of your solar plant. We supply Tier-1 inverters — string (most common), microinverters (panel-level MPPT for shaded roofs), and hybrid (with battery port). All units are MNRE-empanelled, IEC 62109/62116 certified, and come with 5-10 year warranty extendable up to 25 years.",
    specs: ["String / Microinverter / Hybrid variants", "1 kW to 100 kW range", "Dual / Quad MPPT trackers", "Wi-Fi + GPRS cloud monitoring", "99% peak efficiency"],
  },
  {
    slug: "solar-battery",
    title: "Solar Battery & Storage",
    short: "Lithium & Tall Tubular",
    icon: BatteryCharging,
    summary: "Lithium-ion and tall-tubular battery banks for solar storage. Scalable from 100 Ah to MW-scale BESS.",
    benefits: ["Lithium LiFePO4 tech", "Up to 10-year life", "BMS protection", "Scalable modular design"],
    pricing: "Starting ₹35,000",
    image: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=1200&q=70",
    details: "From small 2.5 kWh home backup to 500 kWh commercial BESS — we supply lithium-ion (LiFePO4) and tall-tubular lead-acid batteries for every solar storage need. Lithium offers 10-year life with 90%+ depth of discharge, while tubular is budget-friendly with 3-5 year replacement cycle.",
    specs: ["Lithium LiFePO4: 2.5 / 5 / 10 / 20 kWh modules", "Tall Tubular: 100 / 150 / 200 Ah", "Battery Management System (BMS)", "Parallel scalable", "Deep-cycle technology"],
  },
  {
    slug: "solar-water-heater",
    title: "Solar Water Heater",
    short: "ETC & FPC systems",
    icon: Flame,
    summary: "Hot water all year round with zero running cost. ETC and FPC systems from 100 LPD to 5000 LPD.",
    benefits: ["No electricity cost", "100 - 5000 LPD capacity", "5-year warranty", "BIS certified"],
    pricing: "Starting ₹22,000",
    image: "https://images.unsplash.com/photo-1585671213454-5b3c27c4ae06?w=1200&q=70",
    details: "A solar water heater is the fastest-payback solar product — typically 2-3 years. An ETC (Evacuated Tube Collector) works even on cloudy days and in sub-zero temperatures. FPC (Flat Plate Collector) is more durable for hard water. Both come in pressurised / non-pressurised variants for hotel, hospital and large household use.",
    specs: ["ETC: 100 / 200 / 300 / 500 LPD (household)", "FPC: 500 – 5,000 LPD (commercial)", "Backup electric heater option", "Pressurised + non-pressurised tanks", "Anti-scaling technology"],
  },
  {
    slug: "solar-street-light",
    title: "Solar Street Light",
    short: "Off-grid lighting",
    icon: Lightbulb,
    summary: "Integrated all-in-one solar street lights with motion sensors and smart dusk-to-dawn operation.",
    benefits: ["Zero electricity cost", "Dusk-to-dawn automation", "Motion sensor models", "IP66 weatherproof"],
    pricing: "Starting ₹8,500 / unit",
    image: "https://images.unsplash.com/photo-1565636291841-0ba27f05b9d9?w=1200&q=70",
    details: "All-in-one integrated solar street lights — panel, battery, LED driver and motion sensor in a single sleek housing. Perfect for streets, parks, gated communities, highways, industrial campuses and rural roads. Motion-sensing models dim to 30% when no movement and go full bright on detection — saving 60% battery.",
    specs: ["9W / 12W / 20W / 30W / 60W / 100W LED options", "Lithium LiFePO4 battery built-in", "Motion PIR sensor (optional)", "IP66 weatherproof", "5-year product warranty"],
  },
  {
    slug: "solar-epc",
    title: "Solar EPC",
    short: "Engineer-Procure-Construct",
    icon: Wrench,
    summary: "End-to-end EPC services: design, procurement, installation and commissioning by in-house experts.",
    benefits: ["Turnkey delivery", "In-house design & install", "MNRE-approved partners", "Lifetime tech support"],
    pricing: "Custom Quote",
    image: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=1200&q=70",
    details: "EPC (Engineering, Procurement, Construction) is our core strength. We design, procure and deliver turnkey solar projects for commercial, industrial and utility-scale applications. Our in-house design team uses PVsyst / AutoCAD for layout, shadow analysis and yield projection. No subcontractors — every project is executed by our certified engineers.",
    specs: ["PVsyst-based yield simulation", "Complete project management", "Single-point accountability", "MNRE + IEC + BIS compliant", "Post-commissioning AMC included"],
  },
  {
    slug: "solar-amc",
    title: "Solar AMC / O&M",
    short: "Annual maintenance",
    icon: Settings,
    summary: "Annual Maintenance Contracts and O&M for rooftop and ground-mount solar plants. Cleaning, inspection, monitoring.",
    benefits: ["Quarterly module cleaning", "Inverter health check", "Remote performance monitoring", "Insurance-backed coverage"],
    pricing: "From ₹800 / kW / year",
    image: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?w=1200&q=70",
    details: "Our AMC package keeps your solar investment performing at peak efficiency for 25 years. Covers quarterly deep-cleaning of modules (a dirty panel loses up to 25% output), thermal imaging of panels and inverters, DC/AC wire checks, earthing continuity tests and a monthly performance report. Choose from 1-year, 3-year or 5-year plans. Emergency visits within 24-48 hours for system downtime.",
    specs: ["Quarterly visits (4 per year)", "Module cleaning + IV curve testing", "Inverter firmware updates", "Earthing & SPD check", "Monthly performance report via email"],
  },
  {
    slug: "rooftop-solar",
    title: "Rooftop Solar System",
    short: "Complete rooftop turnkey",
    icon: Home,
    summary: "Complete rooftop solar turnkey — from structural survey to commissioning. Works on RCC, tin-sheet and elevated structures.",
    benefits: ["RCC + tin-sheet compatible", "Elevated mounting option", "Galvanized steel structure", "Wind-load certified up to 180 kmph"],
    pricing: "Starting ₹55,000 / kW*",
    image: "https://images.unsplash.com/photo-1613665813446-82a78c468a1d?w=1200&q=70",
    details: "Our rooftop solar solutions are engineered for Indian roofs — from bare RCC slabs to GI tin-sheet industrial sheds and elevated carport-style mounting. Every installation starts with a structural audit, wind-load calculation (up to 180 kmph compliance per IS 875), and proper ballasting or fastening. We handle waterproofing, cable trays, earthing and DISCOM paperwork.",
    specs: ["Galvanized steel GI structure", "Stainless steel fasteners", "Anti-corrosion coating", "Rooftop walkway for O&M access", "Snow/hail load compliance available"],
  },
  {
    slug: "solar-panels",
    title: "Solar Panels (Modules)",
    short: "Mono / Bifacial / TOPCon",
    icon: Sun,
    summary: "Tier-1 solar modules — Monocrystalline, Bifacial and TOPCon technology from Waaree, Adani, Vikram, Loom and Tata Power Solar.",
    benefits: ["Up to 580W high-wattage modules", "22%+ efficiency available", "25-year performance warranty", "BIS + IEC 61215/61730 certified"],
    pricing: "Starting ₹20 / watt",
    image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=70",
    details: "We stock and supply Tier-1 solar modules across three technologies: Mono PERC (most popular, 19-21% efficiency), Bifacial (generates from front + reflected rear, 5-15% extra yield), and TOPCon (latest N-type, 22%+ efficiency and lower degradation). Wattages from 330W residential to 580W large-format commercial. Every panel ships with a BIS mark and IEC certifications, and carries a 25-year linear performance warranty (minimum 84.8% output at year 25).",
    specs: ["Mono PERC / Bifacial / TOPCon", "330W – 580W range", "Anti-PID and anti-reflective coating", "Salt-mist + ammonia resistance certified", "Tier-1 brands only"],
  },
  {
    slug: "solar-accessories",
    title: "Solar Accessories",
    short: "Cables, connectors, DCDB, ACDB",
    icon: Settings,
    summary: "Complete solar BOS (Balance of System) accessories — DC/AC cables, MC4 connectors, DCDB, ACDB, SPDs, earthing kits, fuses and more.",
    benefits: ["All BOS components in one place", "ISI + BIS + IEC certified", "Wholesale pricing for dealers", "Pan-India delivery"],
    pricing: "Various (₹50 to ₹50,000)",
    image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=1200&q=70",
    details: "A reliable solar plant is only as good as its accessories. We supply the complete Balance-of-System kit — 4 sq mm & 6 sq mm solar DC cables (UV-resistant, Polycab/Finolex/Havells), 3-core AC cables, MC4 connectors (Staubli/genuine), DCDB & ACDB panels with fuses + SPDs (Schneider/Hager), earthing electrodes with chemical compound, lightning arresters, rail-mount junction boxes, cable ties and conduits. All components are ISI/BIS certified and rated for 25-year outdoor use.",
    specs: ["DC cables: 4 / 6 / 10 sq mm", "MC4 connectors (TUV certified)", "DCDB + ACDB with SPDs", "Earthing kits (chemical + conventional)", "Lightning arresters (Class 1/2)", "Cable management accessories"],
  },
  {
    slug: "solar-repair",
    title: "Solar Repair & Reinstallation",
    short: "Fix, replace, relocate",
    icon: Wrench,
    summary: "Repair services for existing solar systems — inverter issues, panel replacement, wiring faults, and complete system reinstallation or relocation.",
    benefits: ["Multi-brand inverter repair", "Panel replacement & warranty claim", "Net-meter re-commissioning", "System relocation to new address"],
    pricing: "From ₹1,500 / visit",
    image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=1200&q=70",
    details: "Bought a solar system from someone else and now facing issues? Or shifting homes? We service, repair and reinstall solar systems of all brands. Common fixes include: inverter error codes (E010, E020, isolation faults), panel hot-spots and micro-cracks, faulty MC4 connectors, net-meter communication issues, ground faults and reverse polarity problems. Full system relocation — dismantling at old site, proper transport, new-site structural survey, and fresh DISCOM application.",
    specs: ["Growatt / Luminous / Microtek / Sungrow / SMA / Fronius service", "IV curve tracing for panels", "Thermal imaging fault detection", "Relocation with new DISCOM paperwork", "Emergency visit within 48 hours"],
  },
];

export const FRANCHISE_MODELS = [
  {
    name: "Solar Dealer",
    badge: "Entry Level",
    investment: "₹1 - 3 Lakh",
    area: "City / Town",
    description: "Ideal for electricians, home-service entrepreneurs and local solar consultants starting their solar business.",
    features: [
      "Brand authorization certificate",
      "Marketing collateral & visiting cards",
      "Lead sharing from CNGTPL Solar website",
      "Basic technical training (3 days)",
      "Product catalog & price list",
      "Digital marketing starter kit",
    ],
    cta: "Apply as Dealer",
  },
  {
    name: "Solar Distributor",
    badge: "Growth",
    investment: "₹5 - 10 Lakh",
    area: "District level",
    description: "For established electrical businesses ready to scale with exclusive district rights and wholesale pricing.",
    features: [
      "District exclusivity",
      "Wholesale pricing on all products",
      "Co-branded marketing campaigns",
      "Advanced technical + sales training",
      "Demo inverter & panel kit",
      "Credit period on purchases",
      "Dedicated relationship manager",
    ],
    cta: "Apply as Distributor",
    highlighted: true,
  },
  {
    name: "Solar Installer / EPC Partner",
    badge: "Premium",
    investment: "₹10 Lakh+",
    area: "Multi-district",
    description: "Full EPC partnership for engineering firms. Execute residential, commercial and MW-scale projects under CNGTPL Solar brand.",
    features: [
      "EPC project assignment",
      "Large commercial lead pipeline",
      "Site engineer certification program",
      "Design & engineering support",
      "Net-metering liaison help",
      "On-site installation training",
      "Priority support & escalation",
      "Subsidy application support",
    ],
    cta: "Apply as EPC Partner",
  },
];

export const FRANCHISE_BENEFITS = [
  { title: "High ROI", desc: "Recover investment in 6–12 months with healthy margins on every installation." },
  { title: "Zero Royalty", desc: "No monthly royalty or hidden fees. Keep 100% of what you earn." },
  { title: "Training Support", desc: "End-to-end sales and technical training. On-site and virtual modules." },
  { title: "Lead Sharing", desc: "Qualified leads from our website and Google campaigns routed to your territory." },
  { title: "Marketing Support", desc: "Co-branded creatives, print material, website listing and social media content." },
  { title: "Product Quality", desc: "Tier-1 modules, certified inverters, MNRE-approved components." },
];

export const PROCESS_STEPS = [
  { step: "01", title: "Free Consultation", desc: "Share your monthly bill. Our expert visits your site and understands needs." },
  { step: "02", title: "Custom Design", desc: "We design a solar system tailored to your roof, load and budget." },
  { step: "03", title: "Fast Installation", desc: "Certified engineers install your system in 3-5 days with minimum disruption." },
  { step: "04", title: "Lifetime Support", desc: "25-year panel warranty. Annual maintenance and real-time monitoring included." },
];

export const TESTIMONIALS = [
  { name: "Rohit Sharma", role: "Homeowner, Noida", text: "My electricity bill went from ₹8,400 to ₹480 per month. CNGTPL Solar's team handled everything — paperwork, subsidy, installation. Zero hassle.", rating: 5 },
  { name: "Priya Mehta", role: "Factory Owner, Gurgaon", text: "We installed a 100 kW system. Payback in 3.2 years, exactly as promised. Monitoring app is excellent.", rating: 5 },
  { name: "Deepak Gupta", role: "School Principal, Delhi", text: "Switching our school to solar was the best decision. Our operating costs dropped by 60%. Thank you CNGTPL Solar!", rating: 5 },
  { name: "Anjali Kapoor", role: "Homeowner, Dwarka", text: "Professional team, transparent pricing. The subsidy was processed without me lifting a finger. Highly recommended.", rating: 5 },
  { name: "Vikram Reddy", role: "Hotelier, Jaipur", text: "CNGTPL Solar delivered a 250 kW plant for our hotel chain. Their O&M team is very responsive.", rating: 5 },
];

export const PARTNERS = [
  { name: "TATA Power", icon: Zap },
  { name: "Adani Solar", icon: Zap },
  { name: "Vikram Solar", icon: Zap },
  { name: "Waaree", icon: Zap },
  { name: "Luminous", icon: BatteryCharging },
  { name: "Microtek", icon: BatteryCharging },
  { name: "UTL Solar", icon: Zap },
  { name: "Polycab", icon: Zap },
  { name: "APN Solar", icon: Sun },
  { name: "Astral Pipe", icon: Droplets },
  { name: "Havells", icon: Zap },
  { name: "Finolex", icon: Zap },
  { name: "Loom Solar", icon: Sun },
  { name: "Growatt", icon: Gauge },
  { name: "Sungrow", icon: Gauge },
  { name: "Exide", icon: BatteryCharging },
];

export const STATS = [
  { value: "50+ MW", label: "Installed Capacity" },
  { value: "12,000+", label: "Happy Customers" },
  { value: "10+ Yrs", label: "Industry Experience" },
  { value: "180+", label: "Cities Served" },
];

export const PROJECTS = [
  { id: 1, title: "5 kW Residential Rooftop", location: "Dwarka, Delhi", category: "Residential", capacity: "5 kW", image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=900&q=70" },
  { id: 2, title: "3 kW Villa Solar", location: "Gurgaon", category: "Residential", capacity: "3 kW", image: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?w=900&q=70" },
  { id: 3, title: "100 kW Factory Plant", location: "Manesar", category: "Commercial", capacity: "100 kW", image: "https://images.unsplash.com/photo-1613665813446-82a78c468a1d?w=900&q=70" },
  { id: 4, title: "250 kW Hotel Chain", location: "Jaipur", category: "Commercial", capacity: "250 kW", image: "https://images.unsplash.com/photo-1559302995-f1c7ac2d9ec8?w=900&q=70" },
  { id: 5, title: "1 MW Ground Mount", location: "Rajasthan", category: "Industrial", capacity: "1 MW", image: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=900&q=70" },
  { id: 6, title: "10 kW School Rooftop", location: "Noida", category: "Commercial", capacity: "10 kW", image: "https://images.unsplash.com/photo-1548337138-e87d889cc369?w=900&q=70" },
  { id: 7, title: "4 kW Home + Battery", location: "Faridabad", category: "Residential", capacity: "4 kW", image: "https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=900&q=70" },
  { id: 8, title: "500 kW Warehouse", location: "Bhiwadi", category: "Industrial", capacity: "500 kW", image: "https://images.unsplash.com/photo-1611365892117-00ac5ef43c90?w=900&q=70" },
  { id: 9, title: "6 kW Duplex Home", location: "Lajpat Nagar, Delhi", category: "Residential", capacity: "6 kW", image: "https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=900&q=70" },
];

export const BLOG = [
  {
    slug: "top-benefits-of-going-solar-in-india-2025",
    title: "Top 7 Benefits of Going Solar in India in 2026",
    excerpt: "From slashing electricity bills to qualifying for government subsidy — here's why 2026 is the best year to install solar.",
    date: "Jan 15, 2026",
    category: "Benefits",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=70",
    content: `Going solar in India is no longer a luxury — it is a smart financial decision. With government subsidies, falling panel prices and rising grid tariffs, the payback period for rooftop solar has dropped to 3-4 years. Here are the top 7 benefits you should know.

1) Slash your electricity bill by 90% — A 5 kW rooftop system generates ~600 units/month, enough to cover the consumption of an average Indian home.

2) Government subsidy up to ₹78,000 — Under the PM Surya Ghar Muft Bijli Yojana, residential systems up to 3 kW get subsidy of ₹30,000/kW (max 2 kW), with additional ₹18,000 for the 3rd kW.

3) 25-year panel warranty — Tier-1 modules now come with 25-year performance warranty (80% output guarantee) and 10-12 year product warranty.

4) Net metering — Export surplus power back to the grid and earn credits, lowering your bill further.

5) Low maintenance — Modern solar plants require only twice-a-year cleaning and visual inspection.

6) Property value boost — Homes with solar sell 4-6% higher than comparable homes without solar.

7) Carbon footprint reduction — A 5 kW system offsets ~6 tons of CO₂ per year.`,
  },
  {
    slug: "pm-surya-ghar-subsidy-guide",
    title: "PM Surya Ghar Muft Bijli Yojana — Subsidy Guide 2026",
    excerpt: "Step-by-step walkthrough of the government subsidy for residential rooftop solar. Eligibility, amount, and how to apply.",
    date: "Feb 02, 2026",
    category: "Subsidy",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=1200&q=70",
    content: `The PM Surya Ghar Muft Bijli Yojana is India's largest residential solar subsidy scheme, targeting 1 crore households.

Subsidy slab:
• Up to 1 kW: ₹30,000
• Up to 2 kW: ₹60,000
• 3 kW and above: ₹78,000 (capped)

Who is eligible?
• Indian citizens with a residential electricity connection
• Roof area sufficient for 1 kW minimum (~100 sqft)
• Applicant must be the owner of the property or have consent

How to apply in 4 steps:
1) Register on the National Portal (pmsuryaghar.gov.in) with your state and DISCOM.
2) Submit your application with consumer number, meter details and identity proof.
3) Get feasibility approval from your DISCOM — usually takes 15-30 days.
4) Once approved, hire a MNRE-empaneled vendor (like CNGTPL Solar). The subsidy amount is transferred directly to your bank account after installation and inspection.

Tip: Choose a certified installer from day one. The subsidy is only disbursed if your vendor is on the MNRE panel and your system uses certified components.`,
  },
  {
    slug: "how-to-choose-solar-panels",
    title: "How to Choose the Right Solar Panels for Your Home",
    excerpt: "Mono vs poly, Tier-1 vs non-Tier-1, efficiency, warranty — a practical buyer's guide.",
    date: "Jan 22, 2026",
    category: "Panel Guide",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?w=1200&q=70",
    content: `Choosing solar panels can feel overwhelming. Here is a straightforward buyer's guide.

1) Mono vs Polycrystalline:
Monocrystalline panels have higher efficiency (19-22%) and perform better in low light. They are the recommended choice for most residential installations today. Polycrystalline is cheaper but less efficient (15-17%).

2) Tier-1 Brands:
Always insist on a Tier-1 module (Bloomberg classification). Examples: Waaree, Vikram Solar, Adani, TATA Power Solar, Loom Solar. Tier-1 means the manufacturer is bankable, high volume and has passed third-party testing.

3) Efficiency Rating:
For the same roof area, a 21% efficient panel generates 20% more power than a 17% panel. Always compare on a per-sqft-per-kWh basis.

4) Warranty Check:
• Product warranty: 10-12 years
• Performance warranty: 25 years (minimum 80% output at year 25)

5) BIS + IEC Certifications:
Look for IEC 61215 and IEC 61730 certifications, plus the BIS mark.

6) Inverter matters too:
Pair your panels with a reputable string inverter (Growatt, Luminous, Microtek, SMA, Fronius). A bad inverter can destroy the ROI of even the best panels.`,
  },
  {
    slug: "solar-for-business-roi",
    title: "Commercial Solar — How Businesses Save Lakhs Every Month",
    excerpt: "Case studies, payback period, tax depreciation benefit and common myths about commercial solar.",
    date: "Feb 10, 2026",
    category: "Commercial",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1611365892117-00ac5ef43c90?w=1200&q=70",
    content: `Commercial establishments in India pay some of the highest electricity tariffs in Asia (₹8-12/unit). This makes rooftop solar an extraordinary investment.

Why commercial solar makes financial sense:

1) Payback in 3-4 years — Unlike residential, commercial customers have high daytime consumption, perfectly matching solar generation. This keeps the payback period very short.

2) Accelerated Depreciation (AD) — Businesses can claim 40% depreciation in year 1 under Section 32 of the Income Tax Act. This is a massive tax saving that further cuts effective cost.

3) GST Input Credit — Commercial users can claim GST input credit on the entire solar system cost (unlike residential).

4) Tariff hedge — Grid tariffs in India rise 5-7% every year. Solar locks in your energy cost for 25 years.

Real example: A hotel in Jaipur installed 250 kW at ₹1.10 crore. With AD benefit, effective cost dropped to ~₹78 lakh. Annual savings: ₹28 lakh. Payback: 2.8 years. Net savings over 25 years: ~₹6 crore.

Common myths busted:
• "Solar only works in summer" — False. Panels actually work better at 25°C than 40°C.
• "Cloudy days mean zero output" — False. Panels generate 10-25% even under cloud cover.
• "Maintenance is expensive" — False. Annual cleaning + inspection costs less than ₹5,000 for a 10 kW system.`,
  },
  {
    slug: "latest-government-solar-schemes-2026",
    title: "Latest Government Solar Schemes in India — 2026 Update",
    excerpt: "Every active central and state solar subsidy scheme you can claim in 2026. PM Surya Ghar, PM-KUSUM, SECI, and state-level incentives.",
    date: "Feb 18, 2026",
    category: "Subsidy",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=70",
    content: `India's solar subsidy ecosystem in 2026 is the most generous it has ever been. Here is a complete round-up of every scheme you should know.

1) PM Surya Ghar: Muft Bijli Yojana (Residential rooftop)
• Target: 1 crore households
• Subsidy: ₹30,000/kW (first 2 kW) + ₹18,000 for 3rd kW — cap ₹78,000
• Applicable for: grid-tied residential systems using MNRE-empaneled vendors
• How to apply: pmsuryaghar.gov.in

2) PM-KUSUM (Solar pumps & agricultural solar)
• Component A: Grid-connected solar pumps — 60% subsidy
• Component B: Standalone off-grid agriculture pumps — 60% central + 30% state in most states
• Component C: Solarisation of existing grid-connected pumps
• Ideal for farmers; can add MW-scale for excess power to grid

3) SECI Tenders for Commercial
• MW-scale reverse-auction tenders by Solar Energy Corporation of India
• Competitive tariffs, 25-year PPAs
• Best for IPPs and commercial developers

4) State-Level Incentives (examples)
• Delhi: Generation-based incentive of ₹2/unit for 3 years on top of central subsidy
• Gujarat: Surya Gujarat scheme with additional ₹10,000/kW state subsidy
• Rajasthan: 80% Accelerated Depreciation + industrial power banking
• Maharashtra: Net metering + additional TOD (time-of-day) benefits

5) Accelerated Depreciation (Commercial)
• 40% AD in year 1 under Section 32 + 20% in year 2
• Effective tax saving: up to 30% of project cost for a 25% income-tax bracket company

6) GST Rate
• Solar modules: 12% GST
• Installation service: 13.8% (blended)
• Commercial users can claim full input tax credit

Tip: CNGTPL Solar handles end-to-end paperwork for PM Surya Ghar and PM-KUSUM, including DISCOM net-metering liaison, so you don't have to chase any official.`,
  },
  {
    slug: "benefits-of-starting-solar-business-2026",
    title: "Why Starting a Solar Business in 2026 is a Goldmine",
    excerpt: "The Indian solar market will cross ₹3,00,000 Cr by 2030. Here's why right now is the best time to become a solar franchise, dealer or EPC partner.",
    date: "Feb 25, 2026",
    category: "Business",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1613665813446-82a78c468a1d?w=1200&q=70",
    content: `India's solar industry is projected to grow at 21% CAGR to 2030, creating a ₹3 lakh crore market opportunity. For entrepreneurs, there has never been a better time to enter.

Why 2026 is the inflection year:

1) Demand is exploding
With the PM Surya Ghar scheme targeting 1 crore homes, and an estimated 27 crore households yet to go solar, the residential pipeline alone is 50+ GW. Every city, every district, every panchayat is a market.

2) Low capital barrier
You don't need a factory. A solar Dealer can start with ₹1-3 lakh. A Distributor with ₹5-10 lakh can own an entire district. An EPC Partner with ₹10 lakh+ can execute commercial projects.

3) High margins
• Residential installation margin: 8-12%
• Commercial: 10-15%
• AMC / O&M (recurring): 25-30%
• Subsidy consulting: ₹3,000-10,000 per customer

4) Government push
Multiple central and state schemes are actively channelizing budgets into solar. MNRE has mandated empanelled vendors — once listed, you automatically qualify for consumer subsidy leads.

5) Recurring revenue
Every system you install becomes a 25-year AMC opportunity. Build a customer base of 500 clients over 2 years and you have an annuity business worth ₹40-60 lakh/year.

6) No seasonality
Unlike retail or real estate, solar has consistent demand across the year. Summer heat pushes bills up (demand driver), winter & monsoon are peak installation seasons.

7) Societal impact
Every 1 kW solar = 1.2 tons CO₂ offset per year. You're building a business that literally cleans the planet.

How to start with CNGTPL Solar:
• Dealer model: 7-day onboarding, free technical training
• Distributor: exclusive district rights, wholesale pricing, credit period
• EPC Partner: get assigned commercial projects directly from our pipeline

Apply at our Franchise page to get a free business plan drafted by our team within 48 hours.`,
  },
  {
    slug: "net-metering-explained-2026",
    title: "Net Metering in India — The Complete 2026 Guide",
    excerpt: "How net metering works, who qualifies, gross vs net vs net-billing, DISCOM approval process and how to maximise exports.",
    date: "Mar 04, 2026",
    category: "Policy",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1559302995-f1c7ac2d9ec8?w=1200&q=70",
    content: `Net metering is the single biggest reason rooftop solar is financially attractive in India. Here is how it works in 2026.

What is net metering?
Your rooftop solar plant generates electricity during the day. If your home is not consuming all of it, the excess flows back into the grid. At night, you draw from the grid. At the end of the billing cycle, you pay only for the NET units (consumed minus exported).

The three variants you'll hear about:

1) Net Metering (most common)
1 unit exported = 1 unit credit. Best for residential consumers.

2) Gross Metering
All solar generation is sold to the grid at a fixed tariff, and you buy back consumption separately. Less common; used for some commercial / PPA arrangements.

3) Net Billing
Export is credited at a lower rate than import tariff (often 50-80%). Some states are transitioning to net-billing for commercial.

Eligibility:
• Residential: up to sanctioned load (typically 1-10 kW connection)
• Commercial: typically up to 500 kW under net metering; higher goes to net-billing
• Industrial: most states cap at 1 MW under net-metering

Approval process (Delhi example):
1) Apply online at your DISCOM portal (BRPL, BYPL, Tata Power DDL)
2) Feasibility inspection within 15 days
3) Sign the net-metering agreement
4) Install bi-directional meter — DISCOM charges nominal fee
5) Commission and synchronise with grid
Timeline: 30-45 days end-to-end

How to maximise net metering benefit:
• Size your system 15-20% above current consumption (safe margin for growth)
• Shift heavy loads (AC, washing machine, geyser) to daytime when solar is generating
• For commercial: align shift timings with solar peak (10am-3pm)

Common pitfalls:
• Using a non-empanelled installer — DISCOM will reject your meter application
• Sizing too large — excess generation caps at sanctioned load, you lose the economic value
• Delayed paperwork — install first, paperwork next is a common mistake. Always apply first.

CNGTPL Solar handles DISCOM paperwork for all installations at zero extra cost.`,
  },
  {
    slug: "solar-battery-storage-guide",
    title: "Solar Battery Storage — Lithium vs Tubular, What to Choose in 2026",
    excerpt: "A practical comparison of lithium-ion and tall-tubular batteries for solar storage. Lifespan, cost, maintenance and recommendation.",
    date: "Mar 11, 2026",
    category: "Storage",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=1200&q=70",
    content: `If you're considering an off-grid or hybrid solar system, battery choice determines 40% of your total cost. Here's the complete breakdown.

Lithium-Ion (LiFePO4) — the premium choice
• Lifespan: 8-10 years (5000+ cycles)
• Depth of discharge: 90-95% usable
• Efficiency: 95-98%
• Maintenance: zero
• Cost: ₹22,000-30,000 per kWh
• Weight: 1/3 of tubular

Tall Tubular (Lead Acid) — the budget workhorse
• Lifespan: 3-5 years (1500-2500 cycles)
• Depth of discharge: 50-60% usable
• Efficiency: 75-85%
• Maintenance: monthly water top-up
• Cost: ₹12,000-15,000 per kWh
• Weight: heavy

Which should you choose?

Choose Lithium if:
• You want 10-year peace of mind
• Space is limited (flats, small rooms)
• You want a maintenance-free setup
• Total lifecycle cost matters (lithium is cheaper over 10 years)

Choose Tall Tubular if:
• Upfront budget is tight
• You're okay with 3-5 year replacement cycle
• You have a dedicated ventilated battery room

Real-world math for a 5 kWh storage system:
• Lithium: ₹1.25 lakh, lasts 10 years → ₹12,500/year
• Tubular: ₹70,000, lasts 4 years → ₹17,500/year (plus monthly maintenance)

The hybrid recommendation:
For most urban homes today, lithium is the winner. Falling prices (18% YoY decline) and 10-year warranties make it the clear choice. Tubular still makes sense in rural/agricultural setups where a dedicated battery room is available.

CNGTPL Solar supplies both Exide, Luminous and BYD lithium batteries — BIS-certified with 10-year warranty.`,
  },
  {
    slug: "solar-maintenance-checklist",
    title: "The Complete Solar Maintenance Checklist — Monthly, Quarterly & Annual",
    excerpt: "A practical maintenance schedule that will help your solar plant produce peak output for 25 years. DIY tips + when to call professionals.",
    date: "Mar 18, 2026",
    category: "Maintenance",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1559302995-f1c7ac2d9ec8?w=1200&q=70",
    content: `A well-maintained solar system produces 95%+ of its rated output. A neglected one can drop to 60-70% within 3 years. Here's the complete maintenance schedule you should follow.

MONTHLY checks (DIY - 10 minutes):
• Visual inspection of panels for dust, bird droppings, leaves
• Check the inverter display for any fault codes
• Verify the monitoring app shows expected daily generation (4-5 units per kW)
• Listen for any unusual humming or buzzing from the inverter
• Check if all LED indicators are green

QUARTERLY (professional - 2 hours):
• Deep cleaning with de-ionised water (avoid tap water — leaves mineral deposits)
• IV curve test of each string to identify underperforming panels
• Torque check of all DC/AC electrical connections
• Inspect junction boxes for moisture ingress
• Check earthing continuity (should be < 1 ohm)
• Review inverter event log for warnings

SEMI-ANNUAL:
• Thermal imaging scan — identifies hotspots and cell failures
• Check mounting structure for rust, loose bolts
• Inspect cable insulation for UV damage or rodent damage
• Test SPDs (surge protection devices)
• Update inverter firmware if available

ANNUAL:
• Complete performance analysis vs expected yield
• Clean and lubricate roof ventilation under panels
• Insurance coverage review
• Check net-meter for tampering or errors
• Wiring continuity and megger test on DC cables

Red flags that need IMMEDIATE attention:
• Any panel showing brown discoloration (hot-spot)
• Inverter showing E101, E020, isolation fault error
• Visible cracks in glass
• Reduction in generation > 15% vs same month last year
• Burning smell from DCDB or ACDB

CNGTPL Solar AMC customers get all of this handled for ₹800-1200 per kW per year. Well worth it considering a single dead panel can cost ₹15,000+ to replace.`,
  },
  {
    slug: "comparing-solar-financing-options",
    title: "CAPEX vs OPEX vs PPA — Which Solar Financing Model is Right For You?",
    excerpt: "A deep-dive into the three main ways to finance a commercial solar plant. Tax implications, cash flow, and when to choose which.",
    date: "Mar 25, 2026",
    category: "Finance",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1604754742629-3e5728249d73?w=1200&q=70",
    content: `If you're a business evaluating solar, the biggest decision is not "which panels" but "how to finance it". Three main models dominate the Indian solar market.

1) CAPEX (Capital Expenditure)
You pay upfront. System is your asset from day 1.
• Full control, full savings (100% of bill reduction)
• Eligible for 40% Accelerated Depreciation (AD) tax benefit
• 25-year ownership — big long-term gain
• Higher upfront cost (₹40-60 lakh for 100 kW commercial)
• Best for: cash-rich businesses with tax liability

Example: 100 kW CAPEX at ₹50 lakh. AD saves ₹6-8 lakh in year 1. Annual saving ₹12 lakh. Payback 3.2 years. 25-year ROI ~400%.

2) OPEX (Operational Expenditure / Renting)
A third party owns the plant on your roof. You pay a monthly fee slightly lower than what you currently pay the DISCOM.
• Zero upfront cost
• Immediate savings from day 1 (usually 10-20%)
• Asset transfers to you after 10-15 years at token value
• Lower total savings over 25 years vs CAPEX
• Best for: cash-constrained companies, tenants with long leases

Example: 100 kW OPEX at ₹6.5/unit (vs DISCOM ₹9/unit). Save ₹2.5/unit × ~1.6 lakh units/year = ₹4 lakh annual savings. After 10 years, asset transfers. 25-year ROI ~150%.

3) PPA (Power Purchase Agreement)
Similar to OPEX but typically for MW-scale. Fixed per-unit tariff for 15-25 years.
• Developer owns, operates and maintains
• You commit to buying generation at contracted rate
• Zero upfront + zero operational headaches
• Best for: large commercial / industrial + government

Which one should you choose?
• Have cash + high tax liability? → CAPEX
• Want zero capex + can't claim AD? → OPEX
• MW+ scale + long-term consumer? → PPA

CNGTPL Solar offers all three models. Our financial team creates a custom model showing year-by-year cashflow for every scenario.`,
  },
  {
    slug: "common-solar-installation-mistakes",
    title: "7 Common Solar Installation Mistakes That Cost Lakhs",
    excerpt: "Shadow over panels, wrong tilt, cheap wire, skipped earthing — these are the most expensive mistakes. Here's how to avoid them.",
    date: "Apr 02, 2026",
    category: "Installation",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=1200&q=70",
    content: `Every week, we audit solar installations done by other vendors. Here are the 7 most common mistakes we see — each one silently losing you thousands of rupees.

MISTAKE 1: Shadow over panels
Even 10% shadow on a single panel can drop the output of the entire string by 40-60%. Common sources: water tanks, AC units, TV antennas, trees, adjacent buildings.
Fix: Shadow analysis before installation. Use microinverters if shadow is unavoidable.

MISTAKE 2: Wrong tilt and orientation
In India, panels should face true south with tilt angle ≈ latitude (±10°). Many installers use whatever angle fits the roof.
Fix: Insist on PVsyst simulation with correct azimuth and tilt. Use elevated structures if roof orientation is wrong.

MISTAKE 3: Undersized DC cables
Cheap installers use 2.5 sq mm instead of proper 4/6 sq mm DC cables. Result: 2-5% resistive losses, overheating, and fire risk.
Fix: 4 sq mm for up to 3 kW, 6 sq mm for up to 10 kW strings. Only UV-resistant, TUV-certified cables.

MISTAKE 4: Skipped or poor earthing
Without proper earthing, any induced voltage (from nearby lightning or faults) can damage your inverter and panels.
Fix: Minimum 3 earthing points (DC, AC, structure). Use chemical earthing for rocky soil. Earth resistance should be < 5 ohms.

MISTAKE 5: No SPD (Surge Protection Device)
Lightning strikes within 1 km can induce voltage spikes on DC cables. Without SPDs, one bad storm can wipe out your entire inverter + panels.
Fix: Install Class 2 SPDs on DC side (before inverter) and AC side (after inverter). Cost: ₹3,000-5,000. Prevents ₹2 lakh+ damage.

MISTAKE 6: Poor waterproofing of roof penetrations
Every bolt through your RCC roof is a potential leak. Many installers skip rubber grommets and sealants.
Fix: EPDM rubber washers + silicone sealant at every penetration point. 5-year waterproofing warranty from installer.

MISTAKE 7: No performance guarantee
"Cheap" installers vanish after handover. No service, no warranty, no guarantees.
Fix: Choose vendors who offer a written performance guarantee — e.g., minimum 1,400 kWh per kW per year, or they credit the shortfall.

CNGTPL Solar provides a written 25-year performance warranty with every installation. If your system under-performs, we fix it at our cost.`,
  },
  {
    slug: "solar-panel-cleaning-guide",
    title: "How to Clean Solar Panels Safely — A Step-by-Step Guide",
    excerpt: "Dirty panels lose 15-25% output. But cleaning them wrong can cause permanent damage. Here's the right way to do it.",
    date: "Apr 09, 2026",
    category: "Maintenance",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1605980413988-9ff24c537935?w=1200&q=70",
    content: `A 5 kW system with dusty panels can lose 100+ units per month. Dust + bird droppings + pollution can cut output by up to 25%. Here's how to clean them safely.

When to clean:
• Dry months (Oct-May in north India): every 15-30 days
• Monsoon months: every 60 days (rain does most of the work)
• After any dust storm or bird-feeding event: immediately

The SAFE cleaning process:

Step 1: Time it right
Clean early morning (before 9 AM) or evening (after 5 PM). Cleaning hot panels with cold water causes thermal shock and can crack the glass.

Step 2: Use soft water
De-ionised water or RO water is best. Regular tap water leaves white mineral streaks that reduce output over time.

Step 3: Use the right tools
• Soft microfiber cloth or silicone squeegee
• Non-abrasive soft-bristle brush for stubborn spots
• Never use: hard brushes, steel wool, pressure washers, or abrasive cleaners

Step 4: The cleaning motion
• Pre-rinse to remove loose dust
• Gentle wiping from top to bottom with microfiber
• Use plain water only. Avoid detergents unless there's oily grime (in which case use a drop of mild baby shampoo in water)
• Final rinse and squeegee dry

Step 5: Safety first
• Never stand on panels — they can break
• Use a proper roof harness if slope > 30°
• Don't clean during rain, storms or high winds
• Shut off the DC isolator before cleaning (not essential but safer)

Common mistakes to avoid:
• Using high-pressure washer (can break the seal at the edges)
• Cleaning at noon with cold water (thermal shock)
• Using acid or vinegar (corrodes the aluminium frame)
• Stepping on panels (microcracks are invisible but permanent)

When to call a professional:
• If your system is > 5 kW
• If the panels are elevated or hard to reach safely
• If there's any hot-spot visible (discoloration)
• Every 6 months for a deep clean with IV curve testing

CNGTPL Solar's cleaning service costs ₹20-50 per panel depending on location and access. For a typical 5 kW (16-panel) system, that's ₹400-800 per visit — money well spent.`,
  },
  {
    slug: "solar-carbon-footprint-environmental-impact",
    title: "How Much CO₂ Does Your Solar System Actually Save?",
    excerpt: "A data-driven look at the real environmental impact of rooftop solar in India. Numbers you can quote with confidence.",
    date: "Apr 16, 2026",
    category: "Environment",
    author: "CNGTPL Solar Team",
    image: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1200&q=70",
    content: `Solar power doesn't just save money — it saves the planet. Here are the real numbers behind the environmental impact of a rooftop solar system in India.

The math:

India's electricity grid emits ~0.82 kg CO₂ per kWh (based on the 2025 grid emission factor). A typical rooftop solar system generates 1,400-1,600 kWh per kW per year.

So 1 kW of solar saves:
• 1,500 kWh × 0.82 kg = 1,230 kg CO₂ per year
• Over 25 years = ~31 tons CO₂ offset

What does 31 tons of CO₂ mean?

• It's equivalent to planting 1,550 fully-grown trees
• Equivalent to NOT burning 3,400 litres of petrol
• Equivalent to NOT burning 14 tons of coal
• Equivalent to taking 7 petrol cars off the road for 1 year

Household-level impact:

A typical 5 kW home solar system:
• Saves 6.15 tons of CO₂ per year
• Over 25 years: 154 tons of CO₂
• Equivalent to planting 7,700 trees
• Equivalent to powering 2 EVs for their entire lifetime

Other environmental benefits:

1) Water saved: Thermal power plants use ~2-3 litres of water per kWh generated. A 5 kW solar system saves 11,250-16,875 litres of water per year. That's one entire swimming pool every 2 years.

2) Air pollution: Indian coal plants emit significant SO₂, NOx and particulate matter. Every kWh from solar reduces air pollution by ~4 grams of SO₂ equivalents. Over 25 years, a 5 kW system prevents ~750 kg of local air pollution.

3) Land impact: While large solar farms use land, rooftop solar uses zero additional land. A 1 kW system on an already-existing roof = ~0 land impact.

4) Reduced transmission loss: India loses ~20% of grid electricity in transmission. Rooftop solar is consumed on-site, completely bypassing this loss.

The business case for ESG compliance:

For SMEs and exporters, installing rooftop solar is increasingly important for ESG reporting. Many international buyers (especially EU-based) now demand Scope-2 emission data. Solar directly reduces Scope-2 emissions and gives you verifiable Renewable Energy Certificates (RECs).

Going solar today is the cheapest CO₂ abatement available globally — under $20 per ton of CO₂ saved, compared to $100+ for most other climate solutions.

Your contribution matters. If just 10% of Indian rooftops went solar, we'd offset ~500 million tons of CO₂ every year — that's more than the entire UK's annual emissions.`,
  },
];

export const FAQS = [
  { q: "How much does solar cost in India?", a: "A 5 kW residential system costs around ₹2.75-3.25 lakh before subsidy. After the ₹78,000 government subsidy, your effective cost is around ₹2 lakh." },
  { q: "How long does installation take?", a: "A standard residential installation (up to 10 kW) is completed in 3-5 working days. Net metering approval takes an additional 2-4 weeks depending on your DISCOM." },
  { q: "Will solar work in monsoon / cloudy days?", a: "Yes. Panels still generate 10-25% of rated output on cloudy days. Your system is grid-tied, so your home continues to receive power seamlessly from the grid when solar is insufficient." },
  { q: "Is there any subsidy?", a: "Yes. Under PM Surya Ghar Muft Bijli Yojana, residential users get up to ₹78,000 subsidy. CNGTPL Solar handles the entire subsidy application on your behalf." },
  { q: "How long is the warranty?", a: "Panels carry a 25-year performance warranty. Inverters carry 5-10 years warranty (extendable). Structure carries a 10-year warranty." },
  { q: "Do you serve my city?", a: "Yes — we serve 180+ cities across Delhi NCR, UP, Haryana, Rajasthan, Punjab, MP and Uttarakhand. Fill the contact form and our regional partner will reach out." },
];
